import React, { useImperativeHandle } from "react";
//#region types
import { DrillDownTableProps, TableExpansionProps } from "models/propTypes/simpleTableProps";
import { TableEnums } from "models/enums";
//#endregion
//#region components
import { Grid, TableContainer, Table } from "@mui/material";
import LoadingWithLogo from "components/common/loading/loadingWithLogo";
import { TableSkeleton } from "components/common/skeletons";
import DrillDownTableBody from "./tableBody";
import DrillDownTableHead from "./tableHead";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const DrillDownTable = <RowType extends Record<string, any>, T extends Record<string, any>>(
  props: React.PropsWithChildren<DrillDownTableProps<RowType, T>>
) => {
  const { loading, rows, rowDisplayType, repeatProperties, tableRef } = props;

  const [autoExpandProps, setAutoExpandProps] = React.useState<TableExpansionProps>();

  useImperativeHandle(tableRef, () => ({
    expandAll: () => {
      setAutoExpandProps({
        expandAll: true,
      });
    },
    collapseAll: () => {
      setAutoExpandProps({
        expandAll: false,
      });
    },
    expandIndex: (index: number, path?: number[]) => {
      setAutoExpandProps({
        expandIndex: {
          index: index,
          path: path ?? [],
        },
      });
    },
  }));

  let tableHtmlRef: HTMLDivElement | null = null;

  const [scrolling, setScrolling] = React.useState<boolean>(false);

  React.useEffect(() => {
    return () => {
      tableHtmlRef = null;
    };
  }, []);

  React.useEffect(() => {
    if (loading && props.rows === undefined) return;
  }, [loading]);

  const onTableScroll = () => {
    if (tableHtmlRef)
      if (tableHtmlRef.scrollLeft > 0 && !scrolling) {
        setScrolling(true);
      } else if (tableHtmlRef.scrollLeft === 0 && scrolling) {
        setScrolling(false);
      }
  };

  const repeatColumnsLength = React.useMemo(() => {
    const nonTitleRows = rowDisplayType
      ? props.rows.filter((r) => rowDisplayType(r) !== TableEnums.RowDisplayType.Title)
      : rows;
    return repeatProperties && nonTitleRows.length ? nonTitleRows[0][repeatProperties.key]?.length : 0;
  }, [rows]);

  return (
    <Grid
      item
      container
      className={clsx(
        cssExports.tableContainerOuter,
        cssExports.drillDownTable,
        scrolling ? cssExports.scrolling : "",
        props.className
      )}
    >
      {loading && rows.length ? <LoadingWithLogo className={cssExports.loadingList} /> : <></>}
      {loading && !rows.length ? (
        <TableSkeleton />
      ) : (
        <TableContainer
          ref={(instance: HTMLDivElement | null) => {
            tableHtmlRef = instance;
          }}
          onScroll={onTableScroll}
        >
          <Table>
            <DrillDownTableHead {...props} repeatColumnLength={repeatColumnsLength} />
            <DrillDownTableBody {...props} repeatColumnLength={repeatColumnsLength} autoExpandProps={autoExpandProps} />
          </Table>
        </TableContainer>
      )}
    </Grid>
  );
};

export default DrillDownTable;
